// WCC360 Year-in-Review — Spotify-Wrapped style
import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toPng } from "html-to-image";
import { ChevronLeft, ChevronRight, Download, Share2, Sparkles, Linkedin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { track } from "@/lib/analytics";
import {
  fetchMyJourney,
  buildSchedule,
  deriveBadges,
  buildGrowthSeries,
  daysBetween,
  type JourneyPayload,
} from "@/lib/journey";

type Slide = {
  id: string;
  bg: string;
  accent: string;
  render: () => JSX.Element;
};

export default function Wrapped() {
  const navigate = useNavigate();
  const [journey, setJourney] = useState<JourneyPayload | null>(null);
  const [handle, setHandle] = useState<string | null>(null);
  const [index, setIndex] = useState(0);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchMyJourney().then(setJourney);
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data } = await supabase.from("profiles").select("referral_handle").eq("id", user.id).maybeSingle();
      if (data?.referral_handle) setHandle(data.referral_handle);
    })();
  }, []);

  const slides: Slide[] = useMemo(() => {
    if (!journey) return [];
    const steps = buildSchedule(journey.membership, journey.milestones);
    const badges = deriveBadges(steps);
    const growth = buildGrowthSeries(steps);
    const start = journey.membership ? new Date(journey.membership.started_at) : new Date();
    const days = daysBetween(start, new Date());
    const completed = steps.filter((s) => s.status === "completed").length;
    const reflections = steps.filter((s) => s.kind === "reflection" && s.status === "completed").length;
    const earnedBadges = badges.filter((b) => b.earned);
    const clarityGain = Math.max(0, (growth.at(-1)?.Clarity ?? 0) - (growth[0]?.Clarity ?? 0));
    const skillsGain = Math.max(0, (growth.at(-1)?.Skills ?? 0) - (growth[0]?.Skills ?? 0));
    const topTheme = "Purpose-driven Builder"; // placeholder until reflections power this

    return [
      {
        id: "intro",
        bg: "from-violet-600 via-fuchsia-600 to-orange-500",
        accent: "text-white",
        render: () => (
          <div className="text-center px-8">
            <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.6 }}>
              <Sparkles className="mx-auto h-16 w-16 mb-6 text-white/90" />
              <div className="text-white/80 uppercase tracking-[0.3em] text-xs mb-3">WCC360 · Year in Review</div>
              <h1 className="font-display text-5xl md:text-7xl font-black text-white leading-tight">
                Your Growth, <br /> Wrapped.
              </h1>
              <p className="mt-6 text-white/85 text-lg max-w-md mx-auto">
                A look back at who you've been becoming this year.
              </p>
            </motion.div>
          </div>
        ),
      },
      {
        id: "days",
        bg: "from-indigo-600 via-blue-600 to-cyan-500",
        accent: "text-white",
        render: () => (
          <div className="text-center px-8">
            <div className="text-white/75 uppercase tracking-[0.3em] text-xs mb-4">You've been on the journey</div>
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.7 }}
              className="font-display text-[120px] md:text-[180px] font-black text-white leading-none"
            >
              {days}
            </motion.div>
            <div className="font-display text-3xl md:text-4xl text-white/90 font-bold mt-2">days</div>
            <p className="mt-6 text-white/80 text-base max-w-sm mx-auto">
              That's {days} days of curiosity, courage and quiet becoming.
            </p>
          </div>
        ),
      },
      {
        id: "milestones",
        bg: "from-emerald-500 via-teal-600 to-cyan-700",
        accent: "text-white",
        render: () => (
          <div className="text-center px-8">
            <div className="text-white/75 uppercase tracking-[0.3em] text-xs mb-4">Milestones completed</div>
            <motion.div
              initial={{ scale: 0.7, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 120 }}
              className="font-display text-[140px] md:text-[200px] font-black text-white leading-none"
            >
              {completed}
              <span className="text-white/60 text-5xl md:text-7xl"> / 13</span>
            </motion.div>
            <p className="mt-6 text-white/85 text-lg max-w-sm mx-auto">
              {reflections} monthly reflections. Every check-in re-mapped your future.
            </p>
          </div>
        ),
      },
      {
        id: "growth",
        bg: "from-pink-500 via-rose-500 to-red-500",
        accent: "text-white",
        render: () => (
          <div className="text-center px-8 w-full">
            <div className="text-white/75 uppercase tracking-[0.3em] text-xs mb-6">Your biggest growth</div>
            <div className="space-y-5 max-w-sm mx-auto">
              <GrowthBar label="Clarity" value={growth.at(-1)?.Clarity ?? 0} delta={clarityGain} />
              <GrowthBar label="Skills" value={growth.at(-1)?.Skills ?? 0} delta={skillsGain} />
              <GrowthBar label="Confidence" value={growth.at(-1)?.Confidence ?? 0} delta={Math.max(0, (growth.at(-1)?.Confidence ?? 0) - (growth[0]?.Confidence ?? 0))} />
            </div>
            <p className="mt-8 text-white/85 max-w-sm mx-auto">
              Compound growth. Small reflections, large shifts.
            </p>
          </div>
        ),
      },
      {
        id: "badges",
        bg: "from-amber-400 via-orange-500 to-pink-600",
        accent: "text-white",
        render: () => (
          <div className="text-center px-8 w-full">
            <div className="text-white/80 uppercase tracking-[0.3em] text-xs mb-3">Achievements unlocked</div>
            <div className="font-display text-7xl md:text-8xl font-black text-white mb-6">{earnedBadges.length}</div>
            <div className="grid grid-cols-4 gap-3 max-w-md mx-auto">
              {badges.slice(0, 8).map((b, i) => (
                <motion.div
                  key={b.key}
                  initial={{ scale: 0, rotate: -30 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ delay: 0.1 + i * 0.06, type: "spring" }}
                  className={`aspect-square rounded-2xl flex flex-col items-center justify-center backdrop-blur ${
                    b.earned ? "bg-white/25 ring-2 ring-white/60" : "bg-white/5 opacity-40"
                  }`}
                >
                  <div className="text-3xl">{b.emoji}</div>
                  <div className="text-[9px] text-white/90 mt-1 px-1 leading-tight">{b.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        ),
      },
      {
        id: "theme",
        bg: "from-slate-900 via-purple-900 to-slate-900",
        accent: "text-white",
        render: () => (
          <div className="text-center px-8">
            <div className="text-white/70 uppercase tracking-[0.3em] text-xs mb-4">Your emerging theme</div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="text-6xl mb-6">💎</div>
              <h2 className="font-display text-4xl md:text-6xl font-black text-white leading-tight">
                {topTheme}
              </h2>
              <p className="mt-6 text-white/75 text-base max-w-sm mx-auto">
                You consistently lean into meaningful work that builds something real.
              </p>
            </motion.div>
          </div>
        ),
      },
      {
        id: "closing",
        bg: "from-cyan-400 via-blue-500 to-violet-700",
        accent: "text-white",
        render: () => (
          <div className="text-center px-8">
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }}>
              <h2 className="font-display text-4xl md:text-5xl font-black text-white leading-tight">
                Career development <br /> is not a one-time decision.
              </h2>
              <p className="mt-6 text-white/90 text-xl font-display font-bold">
                It's a lifelong journey.
              </p>
              <p className="mt-2 text-white/85 text-lg">WCC360 grows with you.</p>
              <div className="mt-8 text-white/70 text-xs uppercase tracking-[0.3em]">
                Rooting You In Your Purpose
              </div>
            </motion.div>
          </div>
        ),
      },
    ];
  }, [journey]);

  const total = slides.length;
  const slide = slides[index];

  function next() { setIndex((i) => Math.min(total - 1, i + 1)); }
  function prev() { setIndex((i) => Math.max(0, i - 1)); }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  async function downloadPng() {
    if (!cardRef.current) return;
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, cacheBust: true });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `wcc360-wrapped-${slide?.id ?? "card"}.png`;
      a.click();
      toast.success("Saved to downloads");
    } catch {
      toast.error("Couldn't export image");
    }
  }

  async function share() {
    if (!cardRef.current) return;
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, cacheBust: true });
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "wcc360-wrapped.png", { type: "image/png" });
      const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean; share?: (d: ShareData) => Promise<void> };
      if (nav.canShare?.({ files: [file] }) && nav.share) {
        await nav.share({ files: [file], title: "My WCC360 Year in Review", text: "My growth, wrapped." });
      } else {
        downloadPng();
      }
    } catch {
      toast.error("Couldn't share");
    }
  }

  if (!journey || !slide) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-muted-foreground">Loading your year…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center p-4 md:p-8">
      {/* Top bar */}
      <div className="w-full max-w-md flex items-center justify-between mb-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/journey")} className="text-white/70 hover:text-white hover:bg-white/10">
          <ChevronLeft className="h-4 w-4 mr-1" /> Journey
        </Button>
        <div className="text-white/60 text-xs font-medium">
          {index + 1} / {total}
        </div>
      </div>

      {/* Progress bars */}
      <div className="w-full max-w-md flex gap-1 mb-3">
        {slides.map((_, i) => (
          <div key={i} className="flex-1 h-0.5 rounded-full bg-white/15 overflow-hidden">
            <div
              className="h-full bg-white transition-all duration-500"
              style={{ width: i < index ? "100%" : i === index ? "100%" : "0%" }}
            />
          </div>
        ))}
      </div>

      {/* Card */}
      <div className="relative w-full max-w-md aspect-[9/16] max-h-[80vh]">
        <AnimatePresence mode="wait">
          <motion.div
            key={slide.id}
            ref={cardRef}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.35 }}
            className={`absolute inset-0 rounded-3xl overflow-hidden shadow-2xl bg-gradient-to-br ${slide.bg} flex items-center justify-center`}
          >
            {/* Soft texture */}
            <div className="absolute inset-0 opacity-20 mix-blend-overlay" style={{
              backgroundImage: "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.5) 0px, transparent 50%), radial-gradient(circle at 80% 80%, rgba(0,0,0,0.3) 0px, transparent 50%)"
            }} />
            <div className="relative z-10 w-full">{slide.render()}</div>
            <div className="absolute bottom-4 right-5 text-white/40 text-[10px] uppercase tracking-widest font-medium">
              wcc360
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Tap zones */}
        <button onClick={prev} className="absolute left-0 top-0 h-full w-1/3 z-20" aria-label="Previous" />
        <button onClick={next} className="absolute right-0 top-0 h-full w-1/3 z-20" aria-label="Next" />
      </div>

      {/* Controls */}
      <div className="w-full max-w-md flex items-center justify-between mt-5">
        <Button variant="ghost" size="icon" onClick={prev} disabled={index === 0} className="text-white/70 hover:text-white hover:bg-white/10">
          <ChevronLeft />
        </Button>
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" onClick={downloadPng}>
            <Download className="h-4 w-4 mr-2" /> PNG
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={!handle}
            onClick={() => {
              if (!handle) return;
              const url = `${window.location.origin}/p/${handle}`;
              track("share_click", { surface: "wrapped", props: { channel: "linkedin", url } });
              window.open(
                `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
                "_blank",
                "noopener,noreferrer,width=620,height=720",
              );
            }}
          >
            <Linkedin className="h-4 w-4 mr-2 text-[#0A66C2]" /> LinkedIn
          </Button>
          <Button size="sm" onClick={share}>
            <Share2 className="h-4 w-4 mr-2" /> Share
          </Button>
        </div>
        <Button variant="ghost" size="icon" onClick={next} disabled={index === total - 1} className="text-white/70 hover:text-white hover:bg-white/10">
          <ChevronRight />
        </Button>
      </div>
    </div>
  );
}

function GrowthBar({ label, value, delta }: { label: string; value: number; delta: number }) {
  return (
    <div>
      <div className="flex justify-between text-white text-sm mb-1.5 font-medium">
        <span>{label}</span>
        <span className="text-white/80">+{delta} pts</span>
      </div>
      <div className="h-3 bg-white/20 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${value}%` }}
          transition={{ duration: 1, ease: "easeOut" }}
          className="h-full bg-white rounded-full"
        />
      </div>
    </div>
  );
}
